import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ArrowUpRight, Calendar } from "lucide-react";
import { BOOKING_URL } from "@/data";
import { track } from "@/hooks/useAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "gpd-chat-session";
const WELCOME = "Hi, I'm the Glebe Park Dental assistant. Ask me about our services, offers, sedation, parking or opening hours — and I'll point you to the right place to book.";

const getSession = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const Bubble = ({ m }) => {
  const parts = m.content.split("[BOOK]");
  return (
    <div data-testid={`chat-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] whitespace-pre-line rounded-xl px-4 py-3 font-sans text-sm leading-relaxed ${m.role === "user" ? "bg-forest text-cream" : "border border-stone/80 bg-cream text-ink"}`}>
        {parts.map((p, i) => (
          <span key={`${i}-${p.slice(0, 12)}`}>
            {p}
            {i < parts.length - 1 && (
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="chat-book-link" onClick={() => track("book_click", { location: "chat-widget" })} className="my-1 inline-flex items-center gap-1 rounded-md bg-forest px-3 py-1.5 text-xs font-medium text-cream hover:bg-pine">
                Book online <ArrowUpRight className="h-3 w-3" />
              </a>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quick, setQuick] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const session = useRef(null);

  useEffect(() => {
    if (!open || session.current) return;
    session.current = getSession();
    fetch(`${API}/chat/${session.current}`)
      .then((r) => r.json())
      .then((d) => {
        setMessages(d.messages?.length ? d.messages : [{ role: "assistant", content: WELCOME }]);
        setQuick(d.quick_questions || []);
      })
      .catch(() => setMessages([{ role: "assistant", content: WELCOME }]));
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    track("chat_message", { length: msg.length });
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
          const chunk = JSON.parse(line.slice(6));
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + chunk };
            return next;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the assistant just now. Please give us a ring on (02) 6295 9460 or [BOOK]" };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-4 z-[90] flex h-[min(600px,calc(100vh-7.5rem))] w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-stone/80 bg-cream shadow-[0_30px_80px_-30px_rgba(36,36,33,0.45)] sm:right-6">
          <div className="flex items-center justify-between border-b border-stone/70 bg-sage px-5 py-4">
            <div>
              <p className="font-serif text-lg text-ink">Ask us anything</p>
              <p className="font-sans text-xs text-fern">Glebe Park Dental · usually replies instantly</p>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Close chat" className="rounded-full p-1.5 text-ink transition-colors hover:bg-ink/10"><X className="h-5 w-5" /></button>
          </div>
          <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => <Bubble key={`${m.ts || i}-${m.role}`} m={m} />)}
            {busy && messages[messages.length - 1]?.content === "" && (
              <p data-testid="chat-typing" className="font-sans text-xs text-fern">Typing…</p>
            )}
            <div ref={endRef} />
          </div>
          {messages.length <= 1 && quick.length > 0 && (
            <div data-testid="chat-quick-questions" className="flex flex-wrap gap-2 border-t border-stone/70 px-4 py-3">
              {quick.map((q) => (
                <button key={q} data-testid="chat-quick-question" onClick={() => send(q)} className="rounded-full border border-stone bg-cream px-3 py-1.5 font-sans text-xs text-ink transition-colors hover:border-forest hover:text-forest">{q}</button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-stone/70 bg-cream px-3 py-3">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              maxLength={1000}
              className="min-w-0 flex-1 rounded-md border border-stone bg-cream px-3 py-2.5 font-sans text-sm text-ink placeholder:text-fern/60 focus:border-forest focus:outline-none"
            />
            <button type="submit" data-testid="chat-send-button" disabled={busy || !input.trim()} aria-label="Send" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-forest text-cream transition-colors hover:bg-pine disabled:opacity-40"><Send className="h-4 w-4" /></button>
          </form>
          <p className="border-t border-stone/50 px-4 py-2 font-sans text-xs leading-snug text-fern/80">General information only — not a diagnosis. In pain? Call (02) 6295 9460.</p>
        </div>
      )}
      <button
        data-testid="chat-toggle-button"
        onClick={() => { setOpen(!open); if (!open) track("chat_open"); }}
        aria-label={open ? "Close chat" : "Ask us anything"}
        className="fixed bottom-6 right-4 z-[90] flex items-center gap-2 rounded-full bg-forest py-3 pl-4 pr-5 font-sans text-sm font-medium text-cream shadow-[0_16px_40px_-12px_rgba(36,54,47,0.6)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-pine sm:right-6"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className={open ? "hidden" : ""}>Ask us anything</span>
      </button>

      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-book-now-button"
        onClick={() => track("book_click", { location: "mobile-sticky-book" })}
        className={`fixed bottom-6 left-4 z-[90] items-center gap-2 rounded-full bg-leaf py-3 pl-5 pr-5 font-sans text-sm font-semibold text-forest shadow-[0_16px_40px_-12px_rgba(0,0,0,0.5)] transition-transform duration-300 hover:-translate-y-0.5 sm:hidden ${open ? "hidden" : "flex"}`}
      >
        <Calendar className="h-5 w-5" />
        Book now
      </a>
    </>
  );
};

export default ChatWidget;
